import { useState, useEffect } from "react";
import Link from "next/link";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import { useRecoilState } from "recoil";

import { engageCalendarMeetings } from "../../atoms/engage";

import {
  daysOfWeek,
  createDaysForCurrentMonth,
  createDaysForNextMonth,
  createDaysForPreviousMonth,
  getEventsForDay,
} from "../../helpers/calendar";
import { apiGetPublicCalendar } from "../../lib/api/engage";

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/solid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

dayjs.extend(isToday);

export default function MonthView({ client }) {
  const [calendarGridDayObjects, setCalendarGridDayObjects] = useState([]);
  const [year, setYear] = useState(dayjs().year());
  const [month, setMonth] = useState(dayjs().month());
  const [yearStr, setYearStr] = useState("");
  const [monthStr, setMonthStr] = useState("");
  const [meetings, setMeetings] = useRecoilState(engageCalendarMeetings);

  useEffect(() => {
    if (client) {
      async function fetchMeetings(month) {
        const result = await apiGetPublicCalendar(client, month);
        if (result) {
          setMeetings(result);
        }
      }
      const year = dayjs().year();
      const month = dayjs().month();
      const tempCurrentMonthDays = createDaysForCurrentMonth(year, month);
      const tempPreviousMonthDays = createDaysForPreviousMonth(
        year,
        month,
        tempCurrentMonthDays
      );
      const tempNextMonthDays = createDaysForNextMonth(
        year,
        month,
        tempCurrentMonthDays
      );
      setCalendarGridDayObjects([
        ...tempPreviousMonthDays,
        ...tempCurrentMonthDays,
        ...tempNextMonthDays,
      ]);
      setMonthStr(dayjs().month(month).format("MMMM"));
      setYearStr(dayjs().year(year).format("YYYY"));
      fetchMeetings(dayjs().toISOString());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const handleMonthNavBack = async () => {
    let nextYear = year;
    let nextMonth = month - 1;
    if (nextMonth === -1) {
      nextMonth = 11;
      nextYear = year - 1;
    }
    setYear(nextYear);
    setMonth(nextMonth);
    const tempCurrentMonthDays = createDaysForCurrentMonth(nextYear, nextMonth);
    const tempPreviousMonthDays = createDaysForPreviousMonth(
      nextYear,
      nextMonth,
      tempCurrentMonthDays
    );
    // console.log(tempPreviousMonthDays);
    const tempNextMonthDays = createDaysForNextMonth(
      nextYear,
      nextMonth,
      tempCurrentMonthDays
    );
    setCalendarGridDayObjects([
      ...tempPreviousMonthDays,
      ...tempCurrentMonthDays,
      ...tempNextMonthDays,
    ]);
    setMonthStr(dayjs().month(nextMonth).format("MMMM"));
    setYearStr(dayjs().year(nextYear).format("YYYY"));
    // API call to get meetings
    setMeetings([]);
    const result = await apiGetPublicCalendar(
      client,
      dayjs(`${nextYear}-${nextMonth + 1}-01`).toISOString()
    );
    if (result) {
      setMeetings(result);
    }
  };

  const handleMonthNavForward = async () => {
    let nextYear = year;
    let nextMonth = month + 1;
    if (nextMonth === 12) {
      nextMonth = 0;
      nextYear = year + 1;
    }
    setYear(nextYear);
    setMonth(nextMonth);
    const tempCurrentMonthDays = createDaysForCurrentMonth(nextYear, nextMonth);
    const tempPreviousMonthDays = createDaysForPreviousMonth(
      nextYear,
      nextMonth,
      tempCurrentMonthDays
    );
    // console.log(tempPreviousMonthDays);
    const tempNextMonthDays = createDaysForNextMonth(
      nextYear,
      nextMonth,
      tempCurrentMonthDays
    );
    setCalendarGridDayObjects([
      ...tempPreviousMonthDays,
      ...tempCurrentMonthDays,
      ...tempNextMonthDays,
    ]);

    setMonthStr(dayjs().month(nextMonth).format("MMMM"));
    setYearStr(dayjs().year(nextYear).format("YYYY"));
    // api call to get meetings
    // API call to get meetings
    setMeetings([]);
    const result = await apiGetPublicCalendar(
      client,
      dayjs(`${nextYear}-${nextMonth + 1}-01`).toISOString()
    );
    if (result) {
      setMeetings(result);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header className="relative z-20 flex items-center justify-between py-2 px-6 lg:flex-none">
        <h1 className="text-lg font-semibold text-gray-900">
          <p>
            {monthStr} {yearStr}
          </p>
        </h1>
        <div className="flex items-center">
          <div className="flex items-center md:items-stretch">
            <button
              className="hidden md:block px-4 py-2 mr-1"
              onClick={() => handleMonthNavBack()}
              aria-label="Previous month"
            >
              <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            <button
              className="hidden md:block px-4 py-2 mr-2"
              onClick={() => handleMonthNavForward()}
              aria-label="Next month"
            >
              <ChevronRightIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>
      <div className="lg:flex lg:flex-auto lg:flex-col border border-gray-300 rounded-sm shadow-sm">
        {/* Days of Week */}
        <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs font-semibold leading-6 text-gray-700 lg:flex-none">
          {daysOfWeek.map((day, index) => (
            <div className="bg-white py-2" key={index}>
              {day}
            </div>
          ))}
        </div>
        {/* Desktop Calendar */}
        <div className="w-full grid grid-cols-7 grid-rows-6">
          {calendarGridDayObjects.map((day, index) => {
            let dayEvents = [];
            if (day.isCurrentMonth) {
              dayEvents = getEventsForDay(meetings, day.dateIso);
            }
            return (
              <div
                className={classNames(
                  day.isCurrentMonth ? "bg-white" : "bg-gray-200 text-gray-500",
                  "relative py-2 px-1 xl:px-1.5 2xl:px-3 md:h-28 2xl:h-32 border border-gray-300"
                )}
                key={index}
              >
                <p
                  className={
                    dayjs(day.dateString).isToday()
                      ? "text-xs 2xl:text-lg flex h-5 w-5 2xl:h-7 2xl:w-7 items-center justify-center rounded-full text-indigo-600 font-semibold"
                      : "text-xs 2xl:text-base"
                  }
                >
                  {day.dayOfMonth.toString()}
                </p>
                {/* Days Meetings */}
                {dayEvents.map((event, index) => {
                  switch (event.publishStatus) {
                    case "draftreview":
                    case "published":
                    case "completed":
                    case "review":
                      return (
                        <Link
                          href="/engage/[client]/[agenda]"
                          as={`/engage/${client}/${event.id}`}
                          key={index}
                        >
                          <a>
                            <div className="bg-transparent hover:bg-indigo-400 rounded-md px-1 xl:px-2 py-0.5 flex items-center mb-0.5 cursor-pointer select-none">
                              <p className="text-[8px] md:text-[9px] hd:text-[12px] font-semibold text-blue-700 hover:text-white">
                                {event.meetingType}{" "}
                                <span className="">
                                  {dayjs(event.scheduleDate).format("h:mm A")}
                                </span>
                              </p>
                            </div>
                          </a>
                        </Link>
                      );
                    case "minutes":
                      return (
                        <Link
                          href="/engage/[client]/minutes/[minutes]"
                          as={`/engage/${client}/minutes/${event.id}`}
                          key={index}
                        >
                          <a>
                            <div className="bg-transparent hover:bg-indigo-400 rounded-md px-1 xl:px-2 py-0.5 flex items-center mb-0.5 cursor-pointer select-none">
                              <p className="text-[8px] md:text-[9px] hd:text-[12px] font-semibold text-blue-700 hover:text-white">
                                {event.meetingType}{" "}
                                <span className="">
                                  {dayjs(event.scheduleDate).format("h:mm A")}
                                </span>
                              </p>
                            </div>
                          </a>
                        </Link>
                      );
                    case "canceled":
                      return (
                        <div className="bg-transparent rounded-md px-1 xl:px-2 py-0.5 flex items-center mb-0.5 select-none">
                          <p className="text-[8px] md:text-[9px] hd:text-[12px] font-semibold text-red-600 line-through">
                            {event.meetingType}{" "}
                            <span className="">
                              {dayjs(event.scheduleDate).format("h:mm A")}
                            </span>
                          </p>
                        </div>
                      );
                    case "adjourned":
                      return (
                        <div className="bg-transparent rounded-md px-1 xl:px-2 py-0.5 flex items-center mb-0.5 select-none">
                          <p className="text-[8px] md:text-[9px] hd:text-[12px] font-semibold text-red-600 line-through">
                            {event.meetingType}{" "}
                            <span className="">
                              {dayjs(event.scheduleDate).format("h:mm A")}
                            </span>
                          </p>
                        </div>
                      );
                    default:
                      return (
                        <div className="bg-transparent rounded-md px-1 xl:px-2 py-0.5 flex items-center mb-0.5 select-none">
                          <p className="text-[8px] md:text-[9px] hd:text-[12px] font-semibold text-black">
                            {event.meetingType}{" "}
                            <span className="">
                              {dayjs(event.scheduleDate).format("h:mm A")}
                            </span>
                          </p>
                        </div>
                      );
                  }
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
